import { getRangeForAllRecordsWithRecordingUuidAndRowUuid, UUID, DbRecording, nowAsISODateTimeString, NameOfIndexForRecordingUuidThenMhtmlCaptureUuid, getRangeForAllRecordsWithRecordingUuidAndRowIndex, NameOfVideoFragmentsIndexForRecordingUuidThenStreamIdThenTimecode } from "./DatabaseTypes";
import * as StateEvents from "../StateEvents";
import { DbEventsTableReader } from "./DbEventsTableReader";
import { DbEventsTableWriter } from "./DbEventsTableWriter";
import { DbRecordingsTableReader } from "./DbRecordingsTableReader";
import { ExtensionSettingsAccessor } from "./DbExtensionSettings";
import { requireEvidenceDatabase } from "./getDatabase";
import { ProcessInfo } from "../ProcessInfo";


export class DbRecordingsTableWriter extends DbRecordingsTableReader {

	static deleteRecording = async (recordingUuid: UUID) => {
		const db = await requireEvidenceDatabase();
		ProcessInfo.log(`Deleting recording ${recordingUuid} started`);
		const recordingUuidRange = getRangeForAllRecordsWithRecordingUuidAndRowUuid(recordingUuid);
		const recordingIndexRange = getRangeForAllRecordsWithRecordingUuidAndRowIndex(recordingUuid);
		// Deletes from each store in parallel, with transactions that are scoped to each store to ensure they don't conflict
		const deleteEventsPromise = (async () => {
			const tx = db.transaction("events", "readwrite").store;
			for await (const cursor of tx.iterate(recordingIndexRange)) {
				await tx.delete(cursor.primaryKey);
			}
		})();
		const deleteVideoFragmentsPromise = (async () => {
			const tx = db.transaction("videoFragments", "readwrite").store;
			for await (const cursor of tx.index(NameOfVideoFragmentsIndexForRecordingUuidThenStreamIdThenTimecode).iterate(recordingUuidRange)) {
				await tx.delete(cursor.primaryKey);
			}
		})();
		const deleteMhtmlCapturesPromise = (async () => {
			const tx = db.transaction("mhtmlCaptures", "readwrite").store;
			for await (const cursor of tx.index(NameOfIndexForRecordingUuidThenMhtmlCaptureUuid).iterate(recordingUuidRange)) {
				await tx.delete(cursor.primaryKey);
			}
		})();
		const deleteHarLogsPromise = (async () => {
			const tx = db.transaction("harLogs", "readwrite").store;
			for await (const cursor of tx.iterate(recordingUuidRange)) {
				await tx.delete(cursor.primaryKey);
			}
		})();
		await Promise.all([deleteEventsPromise, deleteVideoFragmentsPromise, deleteMhtmlCapturesPromise, deleteHarLogsPromise]);
		// Once all the stores indexed by this recording are successfully deleted, delete the recording itself.
		await db.delete("recordings", recordingUuid);

		DbEventsTableReader.removeRecordingUuid(recordingUuid);
		DbEventsTableWriter.removeRecordingUuid(recordingUuid);
		StateEvents.RecordingDeleted.send({recordingUuid});
	};

	static #addRecordingEntry = async (recording: DbRecording) => {
		const db = await requireEvidenceDatabase();
		await db.add("recordings", recording);
		StateEvents.RecordingCreated.send(recording);
	};

	static createRecording = async (args: Omit<DbRecording, "recordingUuid" | "startTimeIso"> & {recordingUuid?: UUID}) => {
		const startTimeIso = nowAsISODateTimeString();
		let {recordingUuid} = args;
		recordingUuid ??= crypto.randomUUID();		
		const newEntry: DbRecording = {
			startTimeIso,
			...args,
			recordingUuid,
			globalSearchTermsNewlineSeparated: (await ExtensionSettingsAccessor.instance).sharedSearchTermField,
		};
		await DbRecordingsTableWriter.#addRecordingEntry(newEntry);
		return newEntry;
	};

}
